import { format } from 'date-fns';
import { HandHelpingIcon } from 'lucide-react';

const REASON_COPY: Record<string, string> = {
  ai_handoff: "Claire couldn't help with this, so she passed it to you.",
  ai_needs_staff_action:
    'The customer needs something only the team can do, so Claire passed it to you.',
  ai_missing_info:
    "Claire couldn't find the answer to the customer's question, so she passed it to you.",
  ai_medical_question:
    'The customer asked a medical question, so Claire passed it to you.',
  ai_wrong_recipient:
    "This message doesn't look like it came from a customer, so Claire left it for you.",
  ai_safety_concern:
    'URGENT: the customer may be at risk of harming themselves. Claire gave the crisis numbers and asked if they are safe. Please contact them now.',
  ai_adverse_reaction:
    'The customer reported a problem after a treatment, so Claire passed it to you straight away.',
  ai_complaint:
    'The customer made a complaint, so Claire passed it to you straight away.',
  callback_requested: 'The customer asked for a call back.',
  ai_silent_handoff: 'Claire chose not to reply to this message.',
  needs_follow_up: 'This needs a follow-up from you.',
  inappropriate_content: 'Claire flagged the content of a message.',
  user_requested_human: 'The customer asked to speak to a person.',
  agent_takeover: 'A team member took over the conversation.',
  max_follow_ups:
    'Claire followed up as many times as she is allowed and got no reply.',
  customer_deferred:
    'The customer asked to pick this up later, so Claire stopped nudging and left it with you.',
  delivery_failed: "Claire's reply couldn't be delivered.",
  non_text_media:
    "The customer sent a voice note or photo that Claire can't read.",
  opt_out_requested:
    'The customer asked not to be contacted. All known channels have been suppressed.',
  wrong_person:
    "Whoever answered says they didn't enquire, so Claire stopped messaging them. Check the contact details on the lead.",
  customer_wrote_after_opt_out:
    "They opted out earlier and have now written again asking for something. Claire didn't reply. Messages to them stay blocked unless they reply START.",
  manual: 'This was handed over manually.',
  lead_form_no_followup:
    "Claire isn't following up on this lead form, so it's yours.",
};

/** Why a lead form was not followed up, when Claire was the one that stopped. */
const DETAIL_COPY: Record<string, string> = {
  no_sender_configured:
    "We couldn't reach this number by WhatsApp or SMS. Check it's saved with the country code.",
  chatbot_disabled: 'Claire is switched off on the channel this lead uses.',
  no_eligible_channel: 'There is no number or consent to message them with.',
  delivery_failed: "The opening message couldn't be delivered.",
  shared_number_held:
    'They are already texting another clinic on our shared number, so Claire could not open a second thread.',
  ad_unreadable:
    "Claire couldn't read the ad this lead came from, so she didn't message them.",
  // Why the opening message did not deliver, when the provider said.
  undeliverable_invalid_number:
    "The phone number on the form can't receive messages. Check it and contact them another way.",
  undeliverable_not_on_whatsapp:
    "Their number isn't on WhatsApp and Claire couldn't reach them another way. Call or text them yourself.",
  undeliverable_opted_out:
    "They've opted out of messages on that channel, so Claire didn't try another.",
  undeliverable_whatsapp_sender:
    'Your WhatsApp account refused the message. Check its connection in Settings.',
  undeliverable_sms_sender:
    'Your SMS sender refused the message. Check it in Settings.',
  in_conversation_elsewhere:
    'They are already talking to you on another channel, so Claire did not message them on a second one.',
  opted_out_elsewhere:
    'They asked not to be contacted on another channel, so Claire did not message them.',
  wrong_person:
    "Whoever answers this number said they didn't enquire, so Claire did not message them.",
};

/**
 * A card in the middle of the thread saying Claire handed the conversation to
 * a person, and why. Sits where the hand-off happened so the reader sees what
 * the customer was asking when Claire stopped.
 */
export function HandoffCard({
  reason,
  detail,
  at,
}: {
  reason?: string | null;
  detail?: string | null;
  at?: string | null;
}) {
  const headline =
    (reason && REASON_COPY[reason]) ?? 'Claire handed this conversation over.';
  const extra = detail ? DETAIL_COPY[detail] : undefined;

  return (
    <section
      aria-label="Handed off to the team"
      className="mx-auto flex w-full max-w-xl items-start gap-3 py-2"
    >
      <span className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
        <HandHelpingIcon className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-medium">
            Claire passed this to you
          </span>
          {at ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {format(new Date(at), 'h:mm a')}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
          {headline}
          {extra ? ` ${extra}` : ''}
        </p>
      </div>
    </section>
  );
}
